import { useAIEdit } from 'hooks/useAIEdit'
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { EuiButtonIcon, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiLoadingContent, EuiPage, EuiPageBody, EuiPanel, EuiSpacer, EuiTabbedContent, EuiTitle, EuiToolTip } from '@elastic/eui'
import config from 'app/config'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { AIEditFlyoutState } from 'api/interfaces/AIEditTypes'
import { MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import { CompetitorProfile, GeographicCluster, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { Persona } from 'api/interfaces/Persona'
import {
  useGetAIPlanBlueprintQuery,
  useEditAIPlanBlueprintNameMutation,
  useEditAIPlanBlueprintMarketAnalysisSectionMutation,
  useAddAIPlanBlueprintCompetitorProfileMutation,
  useEditAIPlanBlueprintCompetitorProfileMutation,
  useRemoveAIPlanBlueprintCompetitorProfileMutation,
  useAddAIPlanBlueprintGeographicClusterMutation,
  useEditAIPlanBlueprintGeographicClusterMutation,
  useRemoveAIPlanBlueprintGeographicClusterMutation,
  useAddAIPlanBlueprintMarketChallengeMutation,
  useEditAIPlanBlueprintMarketChallengeMutation,
  useRemoveAIPlanBlueprintMarketChallengeMutation,
  useRemoveAIPlanBlueprintPersonaMutation,
  useGetAIPlanBlueprintPersonasSectionQuery,
  useUpdateAIPlanBlueprintPersonasIntroductionSectionMutation,
  useHideAIPlanBlueprintPersonasSectionMutation,
  useAddAIPlanBlueprintPersonasCustomSectionMutation,
  useUpdateAIPlanBlueprintPersonasCustomSectionMutation,
  useDeleteAIPlanBlueprintPersonasCustomSectionMutation,
  useUpdateAIPlanBlueprintPersonasPageTitleMutation,
  useGetAIPlanBlueprintMarketAnalysisSectionQuery,
  useUpdateAIPlanBlueprintMarketAnalysisSectionTitleMutation,
  useAddAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useUpdateAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useDeleteAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useAiEditBlueprintMutation,
  useCreateAIPlanBlueprintPublicIdMutation,
  useUpdateAIPlanBlueprintClientPreviewTabsMutation
} from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import AIEditPromptFlyout from 'features/planner/components/AIEditPromptFlyout'
import PersonaList from 'features/planner/components/AIPlan/Personas/PersonaList'
import AIPlannerMarkdownRenderer from 'features/planner/components/AIPlannerMarkdownRenderer'
import EditTitleFlyout from 'features/planner/components/EditTitleFlyout'
import StructuredMarketAnalysis from 'features/planner/components/StructuredMarketAnalysis/StructuredMarketAnalysis'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import CustomizableSection from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/CustomizableSection'
import EditCustomSectionFlyout, { MARKET_ANALYSIS_CUSTOM_SECTION_EXAMPLE_CONTENT } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditPageTitleFlyout'
import PageContent from 'features/planner/pages/MasterCampaignPlanPage/components/PageContent'
import { RenderCustomSections } from 'features/planner/pages/MasterCampaignPlanPage/components/shared/RenderCustomSections'

import { resetEditMode, selectBlueprintEditMode } from './aiPlanBlueprintSlice'
import AIPlanBlueprintClientPreviewTabsFlyout, { AIPlanBlueprintClientPreviewTabsSettings } from './components/AIPlanBlueprintClientPreviewTabsFlyout'
import AIPlanBlueprintHeader from './components/AIPlanBlueprintHeader'
import EditBlueprintNameFlyout from './components/EditBlueprintNameFlyout'
import EditBlueprintPersonaFlyout, { EditBlueprintPersonaFlyoutMode } from './components/EditBlueprintPersonaFlyout'
import ReorderBlueprintPersonasFlyout from './components/ReorderBlueprintPersonasFlyout'

interface AIPlanBlueprintPageParams {
  blueprintId: string
}

interface EditPersonaFlyoutState {
  mode: EditBlueprintPersonaFlyoutMode
  selectedPersona?: Persona
}

type PersonaSectionType = 'introduction'
type PersonaCustomSectionPosition = 'abovePersonaList'

const DEFAULT_PERSONAS_INTRODUCTION_CONTENT = 'The following personas are descriptive of the types of people we will be targeting. While fictional generalizations, they are based on research and market analysis. They will be used both in the targeting of your ad campaigns and in the crafting of your ads.'

const AIPlanBlueprintPage: FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { blueprintId } = useParams<AIPlanBlueprintPageParams>()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectBlueprintEditMode)

  const [selectedTab, setSelectedTab] = useState('market-analysis')
  const [editPersonaFlyoutOpen, setEditPersonaFlyoutOpen] = useState<EditPersonaFlyoutState | null>(null)
  const [isReorderPersonasFlyoutOpen, setIsReorderPersonasFlyoutOpen] = useState(false)
  const [aiEditFlyoutState, setAiEditFlyoutState] = useState<AIEditFlyoutState | null>(null)
  const [editTitleFlyoutState, setEditTitleFlyoutState] = useState<{ currentTitle: string; titleKey?: string } | null>(null)
  const [isEditNameFlyoutOpen, setIsEditNameFlyoutOpen] = useState(false)
  const [editingPersonaSectionType, setEditingPersonaSectionType] = useState<PersonaSectionType | null>(null)
  const [deletingPersonaSectionType, setDeletingPersonaSectionType] = useState<PersonaSectionType | null>(null)
  const [addingPersonaCustomSectionPosition, setAddingPersonaCustomSectionPosition] = useState<PersonaCustomSectionPosition | null>(null)
  const [editingPersonaCustomSection, setEditingPersonaCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [deletingPersonaCustomSection, setDeletingPersonaCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [editingPersonaPageTitle, setEditingPersonaPageTitle] = useState(false)
  const [addingMarketAnalysisCustomSectionPosition, setAddingMarketAnalysisCustomSectionPosition] = useState<MarketAnalysisCustomSectionPosition | null>(null)
  const [editingMarketAnalysisCustomSection, setEditingMarketAnalysisCustomSection] = useState<{ position: MarketAnalysisCustomSectionPosition; index: number } | null>(null)
  const [deletingMarketAnalysisCustomSection, setDeletingMarketAnalysisCustomSection] = useState<{ position: MarketAnalysisCustomSectionPosition; index: number } | null>(null)
  const [isClientPreviewTabsFlyoutOpen, setIsClientPreviewTabsFlyoutOpen] = useState(false)

  const { data: blueprint, isFetching: isBlueprintFetching, isLoading: isBlueprintLoading, error: blueprintError } = useGetAIPlanBlueprintQuery({ blueprintId }, { skip: !blueprintId })

  const [editName, { isLoading: isEditingName }] = useEditAIPlanBlueprintNameMutation()
  const [editMarketAnalysisSection] = useEditAIPlanBlueprintMarketAnalysisSectionMutation()
  const [addCompetitorProfile] = useAddAIPlanBlueprintCompetitorProfileMutation()
  const [editCompetitorProfile] = useEditAIPlanBlueprintCompetitorProfileMutation()
  const [removeCompetitorProfile] = useRemoveAIPlanBlueprintCompetitorProfileMutation()
  const [addGeographicCluster] = useAddAIPlanBlueprintGeographicClusterMutation()
  const [editGeographicCluster] = useEditAIPlanBlueprintGeographicClusterMutation()
  const [removeGeographicCluster] = useRemoveAIPlanBlueprintGeographicClusterMutation()
  const [addMarketChallenge] = useAddAIPlanBlueprintMarketChallengeMutation()
  const [editMarketChallenge] = useEditAIPlanBlueprintMarketChallengeMutation()
  const [removeMarketChallenge] = useRemoveAIPlanBlueprintMarketChallengeMutation()
  const [removePersonaMutation, { isLoading: isRemovingPersona }] = useRemoveAIPlanBlueprintPersonaMutation()

  const { data: personasSection } = useGetAIPlanBlueprintPersonasSectionQuery({ blueprintId }, { skip: !blueprintId })
  const [updatePersonasIntroduction, { isLoading: isUpdatingPersonasIntroduction }] = useUpdateAIPlanBlueprintPersonasIntroductionSectionMutation()
  const [hidePersonasSection, { isLoading: isHidingPersonasSection }] = useHideAIPlanBlueprintPersonasSectionMutation()
  const [addPersonasCustomSection, { isLoading: isAddingPersonasCustomSection }] = useAddAIPlanBlueprintPersonasCustomSectionMutation()
  const [updatePersonasCustomSection, { isLoading: isUpdatingPersonasCustomSection }] = useUpdateAIPlanBlueprintPersonasCustomSectionMutation()
  const [deletePersonasCustomSection, { isLoading: isDeletingPersonasCustomSection }] = useDeleteAIPlanBlueprintPersonasCustomSectionMutation()
  const [updatePersonasPageTitle, { isLoading: isUpdatingPersonasPageTitle }] = useUpdateAIPlanBlueprintPersonasPageTitleMutation()
  const { data: marketAnalysisSection } = useGetAIPlanBlueprintMarketAnalysisSectionQuery({ blueprintId }, { skip: !blueprintId })
  const [updateMarketAnalysisSectionTitle, { isLoading: isUpdatingMarketAnalysisSectionTitle }] = useUpdateAIPlanBlueprintMarketAnalysisSectionTitleMutation()
  const [addMarketAnalysisCustomSection, { isLoading: isAddingMarketAnalysisCustomSection }] = useAddAIPlanBlueprintMarketAnalysisCustomSectionMutation()
  const [updateMarketAnalysisCustomSection, { isLoading: isUpdatingMarketAnalysisCustomSection }] = useUpdateAIPlanBlueprintMarketAnalysisCustomSectionMutation()
  const [deleteMarketAnalysisCustomSection, { isLoading: isDeletingMarketAnalysisCustomSection }] = useDeleteAIPlanBlueprintMarketAnalysisCustomSectionMutation()
  const [aiEditBlueprint] = useAiEditBlueprintMutation()
  const [createPublicId] = useCreateAIPlanBlueprintPublicIdMutation()
  const [updateClientPreviewTabs, { isLoading: isUpdatingClientPreviewTabs }] = useUpdateAIPlanBlueprintClientPreviewTabsMutation()

  const { submit: submitAIEdit, isSubmitting: isAIEditSubmitting } = useAIEdit({
    mutationFn: aiEditBlueprint,
    entityId: blueprintId,
    onSuccess: () => setAiEditFlyoutState(null)
  })

  useEffect(() => {
    if (!currentAccount || !blueprint) return
    if (currentAccount.id !== blueprint.accountId) {
      history.push('/ai/blueprints')
    }
  }, [currentAccount, blueprint, history])

  useEffect(() => {
    return () => {
      dispatch(resetEditMode())
    }
  }, [dispatch])

  const handleEditName = async (name: string) => {
    if (!blueprint || !currentAccount) return
    await editName({ organizationId: blueprint.organizationId, accountId: currentAccount.id, blueprintId: blueprint.id, name })
  }

  const onSectionEdit = (sectionName: string, fieldKey: string, value: string) => {
    editMarketAnalysisSection({ blueprintId, sectionName, data: { [fieldKey]: value } })
  }

  const onAddCompetitorProfile = (profile: CompetitorProfile) => {
    addCompetitorProfile({ blueprintId, competitorProfile: profile })
  }

  const onEditCompetitorProfile = (index: number, profile: CompetitorProfile) => {
    editCompetitorProfile({ blueprintId, index, competitorProfile: profile })
  }

  const onRemoveCompetitorProfile = (index: number) => {
    removeCompetitorProfile({ blueprintId, index })
  }

  const onAddGeographicCluster = (cluster: GeographicCluster) => {
    addGeographicCluster({ blueprintId, geographicCluster: cluster })
  }

  const onEditGeographicCluster = (index: number, cluster: GeographicCluster) => {
    editGeographicCluster({ blueprintId, index, geographicCluster: cluster })
  }

  const onRemoveGeographicCluster = (index: number) => {
    removeGeographicCluster({ blueprintId, index })
  }

  const onAddMarketChallenge = (challenge: MarketChallenge) => {
    addMarketChallenge({ blueprintId, marketChallenge: challenge })
  }

  const onEditMarketChallenge = (index: number, challenge: MarketChallenge) => {
    editMarketChallenge({ blueprintId, index, marketChallenge: challenge })
  }

  const onRemoveMarketChallenge = (index: number) => {
    removeMarketChallenge({ blueprintId, index })
  }

  const onEditPersonaClick = (persona: Persona) => {
    setEditPersonaFlyoutOpen({ mode: EditBlueprintPersonaFlyoutMode.Edit, selectedPersona: persona })
  }

  const onAIAddPersonaClick = () => {
    setAiEditFlyoutState({ targetType: 'AddPersona', targetId: blueprintId, targetLabel: 'Add Persona', promptLabel: 'Describe the new persona you want AdCritter AI to create', promptPlaceholder: 'e.g., A 35-45 year old suburban homeowner who values quality over price, researches extensively online before purchasing, and is motivated by family security and long-term investment' })
  }

  const onDeletePersonaClick = async (personaToDelete: Persona): Promise<void> => {
    if (!blueprint?.id || !currentAccount) return
    await removePersonaMutation({
      organizationId: blueprint.organizationId,
      accountId: currentAccount.id,
      blueprintId: blueprint.id,
      personaName: personaToDelete.name,
      personaRank: personaToDelete.rank
    })
  }

  const onAIEditMarketAnalysisField = (sectionName: string, fieldKey: string, fieldLabel: string) => {
    const sectionData = blueprint?.marketAnalysis?.[sectionName as keyof typeof blueprint.marketAnalysis]
    const targetId = (sectionData as { id?: string })?.id || ''
    setAiEditFlyoutState({ targetType: 'MarketAnalysisField', targetId, sectionName, fieldKey, targetLabel: fieldLabel })
  }

  const onAISectionEdit = (sectionName: string, sectionLabel: string) => {
    const sectionData = blueprint?.marketAnalysis?.[sectionName as keyof typeof blueprint.marketAnalysis]
    const targetId = (sectionData as { id?: string })?.id || ''
    setAiEditFlyoutState({ targetType: 'MarketAnalysisSection', targetId, sectionName, targetLabel: sectionLabel })
  }

  const onEditTitle = (_sectionName: string, _fieldKey: string, currentLabel: string) => {
    setEditTitleFlyoutState({ currentTitle: currentLabel })
  }

  const handleSaveMarketAnalysisCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingMarketAnalysisCustomSectionPosition) {
      const existingSections = marketAnalysisSection?.[addingMarketAnalysisCustomSectionPosition] || []
      await addMarketAnalysisCustomSection({
        blueprintId,
        position: addingMarketAnalysisCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingMarketAnalysisCustomSectionPosition(null)
    } else if (editingMarketAnalysisCustomSection) {
      const existingSections = marketAnalysisSection?.[editingMarketAnalysisCustomSection.position] || []
      await updateMarketAnalysisCustomSection({
        blueprintId,
        position: editingMarketAnalysisCustomSection.position,
        existingSections,
        sectionIndex: editingMarketAnalysisCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingMarketAnalysisCustomSection(null)
    }
  }

  const confirmDeleteMarketAnalysisCustomSection = async () => {
    if (!deletingMarketAnalysisCustomSection) return

    const existingSections = marketAnalysisSection?.[deletingMarketAnalysisCustomSection.position] || []
    await deleteMarketAnalysisCustomSection({
      blueprintId,
      position: deletingMarketAnalysisCustomSection.position,
      existingSections,
      sectionIndex: deletingMarketAnalysisCustomSection.index
    })

    setDeletingMarketAnalysisCustomSection(null)
  }

  const onAIEditPersona = (persona: Persona) => {
    setAiEditFlyoutState({ targetType: 'Persona', targetId: persona.id, targetLabel: persona.name })
  }

  const handleSavePersonasSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingPersonaSectionType) return

    await updatePersonasIntroduction({
      blueprintId,
      title,
      content,
      showTitle
    })

    setEditingPersonaSectionType(null)
  }

  const confirmDeletePersonasSection = async () => {
    if (!deletingPersonaSectionType) return

    await hidePersonasSection({
      blueprintId,
      sectionType: deletingPersonaSectionType
    })

    setDeletingPersonaSectionType(null)
  }

  const handleSavePersonasCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingPersonaCustomSectionPosition) {
      const existingSections = personasSection?.[addingPersonaCustomSectionPosition] || []
      await addPersonasCustomSection({
        blueprintId,
        position: addingPersonaCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingPersonaCustomSectionPosition(null)
    } else if (editingPersonaCustomSection) {
      const existingSections = personasSection?.[editingPersonaCustomSection.position] || []
      await updatePersonasCustomSection({
        blueprintId,
        position: editingPersonaCustomSection.position,
        existingSections,
        sectionIndex: editingPersonaCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingPersonaCustomSection(null)
    }
  }

  const confirmDeletePersonasCustomSection = async () => {
    if (!deletingPersonaCustomSection) return

    const existingSections = personasSection?.[deletingPersonaCustomSection.position] || []
    await deletePersonasCustomSection({
      blueprintId,
      position: deletingPersonaCustomSection.position,
      existingSections,
      sectionIndex: deletingPersonaCustomSection.index
    })

    setDeletingPersonaCustomSection(null)
  }

  const handleSavePersonasPageTitle = async (newTitle: string) => {
    await updatePersonasPageTitle({
      blueprintId,
      pageTitle: newTitle
    })
    setEditingPersonaPageTitle(false)
  }

  const getDeletePersonasCustomSectionTitle = (): string => {
    if (!deletingPersonaCustomSection) return 'Delete Custom Section'
    const sections = personasSection?.[deletingPersonaCustomSection.position] || []
    const section = sections[deletingPersonaCustomSection.index]
    const title = section?.title?.trim()
    return title && title.length > 0 ? `Delete ${title}` : 'Delete Custom Section'
  }

  const getDeletePersonasCustomSectionMessage = (): string => {
    if (!deletingPersonaCustomSection) return 'Are you sure you want to delete this custom section?'
    const sections = personasSection?.[deletingPersonaCustomSection.position] || []
    const section = sections[deletingPersonaCustomSection.index]
    const title = section?.title?.trim()
    return title && title.length > 0 ? `Are you sure you want to delete ${title}?` : 'Are you sure you want to delete this custom section?'
  }

  const onClientPreviewLinkClick = async () => {
    if (!blueprint?.id) return

    let publicId = blueprint.publicId

    if (!publicId) {
      const updatedBlueprint = await createPublicId({ blueprintId: blueprint.id }).unwrap()
      publicId = updatedBlueprint.publicId
    }

    if (publicId) {
      window.open(`${config.campaignPreviewUrl}a/${publicId}`, '_blank')
    }
  }

  const handleSaveClientPreviewTabs = async (settings: AIPlanBlueprintClientPreviewTabsSettings) => {
    if (!blueprint?.id) return
    await updateClientPreviewTabs({
      blueprintId: blueprint.id,
      showMarketAnalysis: settings.showMarketAnalysis,
      showPersonas: settings.showPersonas
    })
    setIsClientPreviewTabsFlyoutOpen(false)
  }

  const isUpdatingAnyPersonasSection = isUpdatingPersonasIntroduction || isAddingPersonasCustomSection || isUpdatingPersonasCustomSection

  if (isBlueprintLoading || (!blueprint && isBlueprintFetching)) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiLoadingContent lines={10} />
        </EuiPageBody>
      </EuiPage>
    )
  }

  if (blueprintError || !blueprint) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiEmptyPrompt title={<h2>Blueprint not found</h2>} body="The AI blueprint you're looking for doesn't exist or you don't have permission to view it." />
        </EuiPageBody>
      </EuiPage>
    )
  }

  const showPersonaActions = editMode

  const getTabFontSize = () => {
    return window.innerWidth > 1102 ? '16px' : '13px'
  }

  const tabs: EuiTabbedContentTab[] = [
    {
      id: 'market-analysis',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Analysis & Strategy</div>,
      content: (
        <PageContent>
          {marketAnalysisSection?.introduction?.hideAll !== true && (
            <>
              <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiTitle size='m'>
                    <h3>{marketAnalysisSection?.introduction?.title || 'Market Analysis & Strategy'}</h3>
                  </EuiTitle>
                </EuiFlexItem>
                {editMode && (
                  <EuiFlexItem grow={false}>
                    <EuiToolTip content='Edit Title'>
                      <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditTitleFlyoutState({ currentTitle: marketAnalysisSection?.introduction?.title || 'Market Analysis & Strategy' })} aria-label='Edit Title Market Analysis & Strategy' size='s' />
                    </EuiToolTip>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
              <div style={{ paddingTop: 24 }}>
                <AIPlannerMarkdownRenderer content={marketAnalysisSection?.introduction?.content || `## ${blueprint.companyName}\n\n---`} />
              </div>
              <EuiSpacer size='xl' />
            </>
          )}
          <StructuredMarketAnalysis
            marketAnalysis={blueprint.marketAnalysis}
            onSectionEdit={onSectionEdit}
            onAIEditClick={onAIEditMarketAnalysisField}
            onAISectionEditClick={onAISectionEdit}
            onEditTitleClick={onEditTitle}
            onEditSectionTitleClick={(sectionName, currentTitle) => {
              const sectionToTitleKey: Record<string, string> = {
                strategicOverview: 'strategicOverviewTitle',
                industryContextAnalysis: 'industryContextAnalysisTitle',
                competitiveLandscape: 'competitiveLandscapeTitle',
                geographicOpportunities: 'geographicOpportunitiesTitle',
                seasonalAndCyclicalPatterns: 'seasonalPatternsTitle',
                coreChallengesInMarket: 'coreChallengesTitle',
                insightsSummary: 'insightsSummaryTitle'
              }
              setEditTitleFlyoutState({ currentTitle, titleKey: sectionToTitleKey[sectionName] })
            }}
            onAddCompetitorProfile={onAddCompetitorProfile}
            onEditCompetitorProfile={onEditCompetitorProfile}
            onRemoveCompetitorProfile={onRemoveCompetitorProfile}
            onAddGeographicCluster={onAddGeographicCluster}
            onEditGeographicCluster={onEditGeographicCluster}
            onRemoveGeographicCluster={onRemoveGeographicCluster}
            onAddMarketChallenge={onAddMarketChallenge}
            onEditMarketChallenge={onEditMarketChallenge}
            onRemoveMarketChallenge={onRemoveMarketChallenge}
            editMode={editMode}
            marketAnalysisSection={marketAnalysisSection}
            onAddCustomSection={position => setAddingMarketAnalysisCustomSectionPosition(position)}
            onEditCustomSection={(position, index) => setEditingMarketAnalysisCustomSection({ position, index })}
            onDeleteCustomSection={(position, index) => setDeletingMarketAnalysisCustomSection({ position, index })}
          />
        </PageContent>
      )
    },
    {
      id: 'personas',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Personas</div>,
      content: (
        <PageContent>
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiTitle size='m'>
                <h3>{personasSection?.pageTitle || 'Persona Prioritization'}</h3>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditingPersonaPageTitle(true)} aria-label='Edit Title Persona Prioritization' size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
          <EuiHorizontalRule />

          {!personasSection?.introduction?.hideAll && (
            <>
              <CustomizableSection section={personasSection?.introduction} defaultTitle='' defaultContent={DEFAULT_PERSONAS_INTRODUCTION_CONTENT} defaultShowTitle={false} separator='spacer' onEdit={() => setEditingPersonaSectionType('introduction')} onDelete={() => setDeletingPersonaSectionType('introduction')} showActions={editMode} />
              <EuiSpacer size='xl' />
            </>
          )}

          <RenderCustomSections sections={personasSection?.abovePersonaList} position={'abovePersonaList' as PersonaCustomSectionPosition} editMode={editMode} onEdit={(position: PersonaCustomSectionPosition, index: number) => setEditingPersonaCustomSection({ position, index })} onDelete={(position: PersonaCustomSectionPosition, index: number) => setDeletingPersonaCustomSection({ position, index })} />
          {editMode && (
            <>
              <AddCustomSectionButton position={'abovePersonaList' as PersonaCustomSectionPosition} onClick={() => setAddingPersonaCustomSectionPosition('abovePersonaList')} />
              <EuiSpacer size='xl' />
            </>
          )}

          <PersonaList
            personas={blueprint.personas || []}
            onEditPersonaClick={showPersonaActions ? onEditPersonaClick : undefined}
            onAIEditPersonaClick={showPersonaActions ? onAIEditPersona : undefined}
            onAIAddPersonaClick={showPersonaActions ? onAIAddPersonaClick : undefined}
            onReorderPersonasClick={showPersonaActions ? () => setIsReorderPersonasFlyoutOpen(true) : undefined}
            onDeletePersonaClick={showPersonaActions ? onDeletePersonaClick : undefined}
            onDeletePersonaLoading={isRemovingPersona}
            showActions={showPersonaActions}
          />
        </PageContent>
      )
    },
    {
      id: 'client-preview-link',
      name: (
        <span className='euiFlexGroup euiFlexGroup--gutterSmall euiFlexGroup--alignItemsCenter euiFlexGroup--directionRow' style={{ margin: -2, fontSize: getTabFontSize() }}>
          <div className='euiFlexItem euiFlexItem--flexGrowZero' style={{ paddingLeft: '.5rem', paddingRight: '.5rem', margin: 2 }}>
            Client Preview Link
          </div>
          <EuiButtonIcon
            className='euiFlexItem euiFlexItem--flexGrowZero'
            style={{ margin: 2 }}
            iconType='pencil'
            onClick={(e: any) => {
              e.stopPropagation()
              setIsClientPreviewTabsFlyoutOpen(true)
            }}
            aria-label='Edit Campaign Preview Tabs'
            size='s'
          />
        </span>
      ),
      content: (
        <PageContent>
          <EuiLoadingContent lines={10} />
        </PageContent>
      )
    }
  ]

  return (
    <React.Fragment>
      <Helmet>
        <title>AI Blueprint - {blueprint.companyName}</title>
      </Helmet>

      <EuiPage className='euiPage--platform'>
        <EuiPageBody style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--euiHeaderHeightCompensation))' }}>
          <AIPlanBlueprintHeader blueprintName={blueprint.name || blueprint.companyName} companyName={blueprint.companyName} onEditNameClick={() => setIsEditNameFlyoutOpen(true)} />

          <EuiPanel>
            <EuiSpacer size='m' />
            <EuiTabbedContent
              tabs={tabs}
              selectedTab={tabs.find(tab => tab.id === selectedTab) || tabs[0]}
              onTabClick={tab => {
                if (tab.id === 'client-preview-link') {
                  onClientPreviewLinkClick()
                  return
                }
                dispatch(resetEditMode())
                setSelectedTab(tab.id)
              }}
            />
          </EuiPanel>
        </EuiPageBody>
      </EuiPage>

      {editPersonaFlyoutOpen && <EditBlueprintPersonaFlyout editMode={editPersonaFlyoutOpen.mode} selectedPersona={editPersonaFlyoutOpen.selectedPersona} onClose={() => setEditPersonaFlyoutOpen(null)} />}

      {isReorderPersonasFlyoutOpen && <ReorderBlueprintPersonasFlyout onClose={() => setIsReorderPersonasFlyoutOpen(false)} />}

      {aiEditFlyoutState && <AIEditPromptFlyout targetType={aiEditFlyoutState.targetType} targetLabel={aiEditFlyoutState.targetLabel} promptLabel={aiEditFlyoutState.promptLabel} promptPlaceholder={aiEditFlyoutState.promptPlaceholder} onSubmit={(_prompt: string, _constraints?: unknown, deepThink?: boolean) => submitAIEdit(_prompt, aiEditFlyoutState, deepThink)} onClose={() => setAiEditFlyoutState(null)} isSubmitting={isAIEditSubmitting} />}

      {editTitleFlyoutState && (
        <EditTitleFlyout
          currentTitle={editTitleFlyoutState.currentTitle}
          onSave={async (newTitle: string) => {
            if (editTitleFlyoutState.titleKey) {
              await updateMarketAnalysisSectionTitle({ blueprintId, titleKey: editTitleFlyoutState.titleKey, titleValue: newTitle })
            }
            setEditTitleFlyoutState(null)
          }}
          onClose={() => setEditTitleFlyoutState(null)}
          isSaving={isUpdatingMarketAnalysisSectionTitle}
        />
      )}

      {isEditNameFlyoutOpen && <EditBlueprintNameFlyout currentName={blueprint.name || blueprint.companyName} onSave={handleEditName} onClose={() => setIsEditNameFlyoutOpen(false)} isLoading={isEditingName} />}

      {editingPersonaSectionType && <EditCustomSectionFlyout section={personasSection?.introduction} defaultTitle='' defaultContent={DEFAULT_PERSONAS_INTRODUCTION_CONTENT} defaultShowTitle={false} flyoutTitle='Edit Introduction' onSave={handleSavePersonasSection} onClose={() => setEditingPersonaSectionType(null)} isLoading={isUpdatingAnyPersonasSection} />}

      {(addingPersonaCustomSectionPosition || editingPersonaCustomSection) && (
        <EditCustomSectionFlyout
          section={editingPersonaCustomSection ? personasSection?.[editingPersonaCustomSection.position]?.[editingPersonaCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingPersonaCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSavePersonasCustomSection}
          onClose={() => {
            setAddingPersonaCustomSectionPosition(null)
            setEditingPersonaCustomSection(null)
          }}
          isLoading={isUpdatingAnyPersonasSection}
        />
      )}

      {deletingPersonaSectionType && <AcConfirmModal message='Are you sure you want to delete this section?' title='Delete Introduction' onCancel={() => setDeletingPersonaSectionType(null)} onConfirm={confirmDeletePersonasSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingPersonasSection} confirmButtonDisabled={isHidingPersonasSection} />}

      {deletingPersonaCustomSection && <AcConfirmModal message={getDeletePersonasCustomSectionMessage()} title={getDeletePersonasCustomSectionTitle()} onCancel={() => setDeletingPersonaCustomSection(null)} onConfirm={confirmDeletePersonasCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingPersonasCustomSection} confirmButtonDisabled={isDeletingPersonasCustomSection} />}

      {editingPersonaPageTitle && <EditPageTitleFlyout currentTitle={personasSection?.pageTitle || 'Persona Prioritization'} defaultTitle='Persona Prioritization' onSave={handleSavePersonasPageTitle} onClose={() => setEditingPersonaPageTitle(false)} isLoading={isUpdatingPersonasPageTitle} />}

      {(addingMarketAnalysisCustomSectionPosition || editingMarketAnalysisCustomSection) && (
        <EditCustomSectionFlyout
          section={editingMarketAnalysisCustomSection ? (marketAnalysisSection as any)?.[editingMarketAnalysisCustomSection.position]?.[editingMarketAnalysisCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent={MARKET_ANALYSIS_CUSTOM_SECTION_EXAMPLE_CONTENT}
          defaultShowTitle={true}
          flyoutTitle={addingMarketAnalysisCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveMarketAnalysisCustomSection}
          onClose={() => {
            setAddingMarketAnalysisCustomSectionPosition(null)
            setEditingMarketAnalysisCustomSection(null)
          }}
          isLoading={isAddingMarketAnalysisCustomSection || isUpdatingMarketAnalysisCustomSection}
          hideTitleToggle
          useMarkdownEditor
          previewRenderer={AIPlannerMarkdownRenderer}
        />
      )}

      {deletingMarketAnalysisCustomSection && <AcConfirmModal message='Are you sure you want to delete this custom section?' title='Delete Custom Section' onCancel={() => setDeletingMarketAnalysisCustomSection(null)} onConfirm={confirmDeleteMarketAnalysisCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingMarketAnalysisCustomSection} confirmButtonDisabled={isDeletingMarketAnalysisCustomSection} />}

      {isClientPreviewTabsFlyoutOpen && (
        <AIPlanBlueprintClientPreviewTabsFlyout
          currentSettings={{
            showMarketAnalysis: blueprint.clientPreviewShowMarketAnalysis ?? true,
            showPersonas: blueprint.clientPreviewShowPersonas ?? true
          }}
          onSave={handleSaveClientPreviewTabs}
          onClose={() => setIsClientPreviewTabsFlyoutOpen(false)}
          isLoading={isUpdatingClientPreviewTabs}
        />
      )}
    </React.Fragment>
  )
}

export default AIPlanBlueprintPage
